/**
 * Visit schedule API.
 */

import { apiClient } from '@/shared/lib/axios';
import { clinicApiPath } from '@/shared/lib/apiPath';
import { SERVICE_TYPE_A2 } from '@/constants/serviceType';
import type {
  VisitScheduleResponse,
  VisitDetailResponse,
  VisitScheduleParams,
  VisitDraftPayload,
  VisitScheduleEvent,
} from '@/pages/main/visitSchedule/types';
import type { VisitErrorItem } from '@/types/visitClinic.types';

/**
 * Visit-schedule dropdown row. 1 row = 1 clinic_location_massager (CLM):
 *   - `id` = CLM id (the URL/select value).
 *   - `massager.id` = real massagers.id (sent on every API call as `massager_id`).
 *   - `clinic_location` = the location address shown under the dropdown.
 */
export interface VisitScheduleMassager {
  id: number;
  massager_id: number;
  clinic_location_id: number | null;
  service_type?: string | null;
  available_from?: string | null;
  available_to?: string | null;
  massager: {
    id: number;
    serial_number: number | null;
    name: string;
    name_kana?: string | null;
    service_type?: string | null;
  } | null;
  clinic_location: {
    id: number;
    location_name: string | null;
    address1: string | null;
    address2: string | null;
    address3: string | null;
  } | null;
}

export interface VisitScheduleVisit {
  id: number;
  massager_id: number;
  clinic_location_id?: number | null;
  patient_id: number;
  serial_number: string;
  patient_name: string;
  visit_datetime_from: string;
  visit_datetime_to: string;
  visit_minutes: number;
  is_error: boolean;
  is_locked: boolean;
  same_visit_id?: number | null;
  group_count?: number | null;
  patient_address_id?: number | null;
  patient_address_name?: string | null;
  patient_base_address1?: string | null;
  patient_base_address2?: string | null;
  patient_base_address3?: string | null;
  patient_base_address_name?: string | null;
  massager_remarks?: string | null;
  electric_treatment?: number | null;
  is_not_hot_treatment?: boolean | null;
  error_messages?: string | null;
  error_items?: VisitErrorItem[] | null;
  checkin_status?: number;
  massager_started_at?: string | null;
  massager_ended_at?: string | null;
  is_suddenly_visit?: boolean;
  is_copied?: boolean;
  copy_visit_id?: number | null;
  hospital_name?: string | null;
  doctor_name?: string | null;
  visit_reason?: string | null;
  alignment_date?: string | null;
  distance?: number | string | null;
  is_non_charge?: boolean;
  is_non_visit_treatment?: boolean;
  is_non_distance_price_visit?: boolean;
  /** 往療費不要 — consent.is_not_transport_cost (No942) */
  is_not_transport_cost?: boolean;
  /** consent.walking_condition_id of the consent covering this visit date (2 = 往療を必要としない). */
  walking_condition_id?: number | null;
  /** 突発的な往療 checkbox 表示条件: 訪問日の時点で walking_condition_id=2 の同意書が有効. Backend computed. */
  patient_suddenly_visit_eligible?: boolean;
  /** 電療 (A2) / 温罨法・電療 (R2) checkbox 表示条件: 訪問日の時点で該当設定のある同意書が有効. Backend computed. */
  patient_electric_treatment_eligible?: boolean;
}

export interface VisitScheduleMassagersResponse {
  success: boolean;
  data: VisitScheduleMassager[];
}

export interface VisitScheduleIndexResponse {
  success: boolean;
  data: VisitScheduleVisit[];
}

export interface VisitScheduleDetailRow {
  number: number;
  from_address: string;
  from_name: string;
  to_address: string;
  to_full_address?: string | null;
  to_name: string;
  to_latitude: number | null;
  to_longitude: number | null;
  distance: number | string | null;
  is_error: boolean;
  is_locked: boolean;
  is_non_charge: boolean;
  is_distance_from_massager: boolean;
  visit_id: number;
  visit_datetime_from: string | null;
  visit_datetime_to: string | null;
}

export interface VisitScheduleDayDetail {
  visitdate: string;
  service_type: 'A2' | 'R2';
  massager: {
    id: number | null;
    name: string | null;
    address: string;
  };
  is_locked: boolean;
  rows: VisitScheduleDetailRow[];
}

export interface VisitScheduleDetailResponse {
  success: boolean;
  data: VisitScheduleDayDetail;
}

export interface PrintWeeklySchedulePayload {
  massager_id: number;
  clinic_location_id?: number | null;
  visitdate: string;
  service_type?: 'A2' | 'R2';
}

/** Payload sent for 合計提出: the entire schedule on the calendar. */
export interface SubmitTotalPayload {
  visits: Array<{
    id?: number | string | null;
    patient_id: number;
    massager_id?: number;
    clinic_location_id?: number | null;
    visit_datetime_from: string;
    visit_datetime_to: string;
    visit_minutes: number;
    patient_address_name?: string;
    patient_address_id?: number | null;
    is_copied?: boolean;
    copy_visit_id?: number | null;
    massager_remarks?: string;
    electric_treatment?: number | null;
    is_not_hot_treatment?: boolean | null;
    is_suddenly_visit?: boolean;
    hospital_name?: string;
    doctor_name?: string;
    visit_reason?: string;
    alignment_date?: string;
  }>;
  /** 当月再更新: true → backend recalculates the entire month instead of only the week */
  is_full_month?: boolean;
  /** Base date used to determine the month when is_full_month = true */
  visitdate?: string;
}

export async function getVisitScheduleMassagers(
  siteType: string,
  clinicNumber: string,
  serviceType: string = SERVICE_TYPE_A2,
  visitdate?: string
): Promise<VisitScheduleMassagersResponse> {
  const path = clinicApiPath(siteType, clinicNumber, '/visits/schedule/massagers');
  const res = await apiClient.get<VisitScheduleMassagersResponse>(path, {
    params: visitdate ? { visitdate } : undefined,
    headers: { 'X-Service-Type': serviceType },
  });
  return res.data;
}

export async function getVisitScheduleVisits(
  siteType: string,
  clinicNumber: string,
  params: { massager_id: number; visitdate: string; clinic_location_id?: number | null },
  serviceType: string = SERVICE_TYPE_A2
): Promise<VisitScheduleIndexResponse> {
  const path = clinicApiPath(siteType, clinicNumber, '/visits/schedule');
  const res = await apiClient.get<VisitScheduleIndexResponse>(path, {
    params,
    headers: { 'X-Service-Type': serviceType },
  });
  return res.data;
}

/**
 * Existence check for the copy-week guard. Like `getVisitScheduleVisits`, this
 * excludes 来院 / Treatment rows (`previous_visit_id = -1`) so Treatment data
 * does not block 次週にコピー when no Home Visit exists in next week.
 * Returns the boolean directly (unwraps the Laravel `{success, data: {has_data}}`
 * envelope) so callers can `if (await ...)` without nested access.
 */
export async function checkVisitScheduleWeekHasData(
  siteType: string,
  clinicNumber: string,
  params: { massager_id: number; visitdate: string; clinic_location_id?: number | null },
  serviceType: string = SERVICE_TYPE_A2
): Promise<boolean> {
  const path = clinicApiPath(siteType, clinicNumber, '/visits/schedule/has-data');
  const res = await apiClient.get<{ success: boolean; message: string; data: { has_data: boolean } }>(
    path,
    {
      params,
      headers: { 'X-Service-Type': serviceType },
    }
  );
  return Boolean(res.data?.data?.has_data);
}

export async function getVisitScheduleDayDetail(
  siteType: string,
  clinicNumber: string,
  params: { massager_id: number; visitdate: string; clinic_location_id?: number | null },
  serviceType: string = SERVICE_TYPE_A2
): Promise<VisitScheduleDetailResponse> {
  const path = clinicApiPath(siteType, clinicNumber, '/visits/schedule/detail');
  const res = await apiClient.get<VisitScheduleDetailResponse>(path, {
    params,
    headers: { 'X-Service-Type': serviceType },
  });
  return res.data;
}

export async function getVisitSchedule(
  siteType: string,
  clinicNumber: string,
  params?: VisitScheduleParams
): Promise<VisitScheduleResponse> {
  const response = await apiClient.get<VisitScheduleResponse>(
    clinicApiPath(siteType, clinicNumber, '/visits/schedule'),
    { params }
  );
  return response.data;
}

export async function getVisitDetail(
  siteType: string,
  clinicNumber: string,
  id: string | number
): Promise<VisitDetailResponse> {
  const response = await apiClient.get<VisitDetailResponse>(
    clinicApiPath(siteType, clinicNumber, `/visits/${id}`)
  );
  return response.data;
}

export async function createVisitDraft(
  siteType: string,
  clinicNumber: string,
  payload: VisitDraftPayload
): Promise<{ success: boolean; data?: VisitScheduleEvent; message?: string }> {
  const response = await apiClient.post(
    clinicApiPath(siteType, clinicNumber, '/visits'),
    payload
  );
  return response.data;
}

export async function updateVisitDraft(
  siteType: string,
  clinicNumber: string,
  id: string | number,
  payload: VisitDraftPayload
): Promise<{ success: boolean; data?: VisitScheduleEvent; message?: string }> {
  const response = await apiClient.put(
    clinicApiPath(siteType, clinicNumber, `/visits/${id}`),
    payload
  );
  return response.data;
}

export async function submitVisitTotal(
  siteType: string,
  clinicNumber: string,
  payload: SubmitTotalPayload
): Promise<{ success: boolean; data?: { submitted?: boolean; message?: string }; message?: string }> {
  const response = await apiClient.post(
    clinicApiPath(siteType, clinicNumber, '/visits/submit-total'),
    payload
  );
  return response.data;
}

/** 距離編集: visit row returned from distance-change API */
export interface DistanceChangeRow {
  id: number;
  number: number;
  patient_name: string;
  serial_number: string | number | null;
  from_address: string;
  from_name: string;
  from_latitude: number | null;
  from_longitude: number | null;
  to_address: string;
  to_full_address?: string | null;
  to_address3: string | null;
  location_name: string | null;
  to_name: string;
  to_latitude: number | null;
  to_longitude: number | null;
  distance: number | string | null;
  is_locked: boolean;
  rezept_status: number;
  is_rezept_finalized: boolean;
  is_non_charge: boolean;
  is_non_visit_treatment: boolean;
  is_non_distance_price_visit: boolean;
  is_not_transport_cost: boolean;
  walking_condition_id: number | null;
  group_number: number | null;
  /** Number of patients at the same address treated by all massagers at the same facility on that day. Read-only. */
  group_count: number | null;
  same_visit_id: number | null;
  is_distance_from_massager: boolean;
  is_error: boolean;
  error_messages: string | null;
  error_items: VisitErrorItem[] | null;
}

export interface DistanceChangeData {
  visitdate: string;
  service_type: 'A2' | 'R2';
  massager: {
    id: number | null;
    name: string;
    address: string;
    latitude: number | null;
    longitude: number | null;
  };
  clinic_location?: {
    id: number;
    name: string;
    address1: string;
    address2: string;
    address3: string;
    latitude: number | null;
    longitude: number | null;
  } | null;
  is_locked: boolean;
  is_enable_select_pre_address: boolean;
  rows: DistanceChangeRow[];
}

export interface DistanceSavePayload {
  massager_id: number;
  visitdate: string;
  /**
   * Visit ids whose latlon was changed by user pin-drag in this session.
   * BE writes these to latlon_caches so the user-corrected coordinates
   * persist for future visits at the same patient address.
   */
  latlon_visit_ids?: number[];
  visits: Array<{
    id: number;
    distance?: number | null;
    latitude?: number | null;
    longitude?: number | null;
    is_non_charge?: boolean;
    is_non_visit_treatment?: boolean;
    group_number?: number | null;
    is_distance_from_massager?: boolean;
    /**
     * True when user manually changed something that affects distance for this
     * row in this session (typed distance / changed 拠点 dropdown / dragged
     * pin). BE writes to distance_caches only when this is true — mirrors A2
     * VisitController.php:2510 ($request->edit_manual[$index] gate).
     */
    edit_manual?: boolean;
  }>;
}

export async function getDistanceChangeData(
  siteType: string,
  clinicNumber: string,
  params: { massager_id: number; visitdate: string; clinic_location_id?: number | null },
  serviceType: string = SERVICE_TYPE_A2
): Promise<{ success: boolean; data: DistanceChangeData }> {
  const path = clinicApiPath(siteType, clinicNumber, '/visits/distance-change');
  const res = await apiClient.get(path, {
    params,
    headers: { 'X-Service-Type': serviceType },
  });
  return res.data;
}

export async function saveDistanceChanges(
  siteType: string,
  clinicNumber: string,
  payload: DistanceSavePayload,
  serviceType: string = SERVICE_TYPE_A2
): Promise<{ success: boolean; message?: string }> {
  const path = clinicApiPath(siteType, clinicNumber, '/visits/distance-save');
  const res = await apiClient.post(path, payload, {
    headers: { 'X-Service-Type': serviceType },
  });
  return res.data;
}

/**
 * Check if patient already has a 来院 (clinic visit) on the given date(s).
 * Ported from RA2 VisitController::treatmentIsExist.
 */
export async function checkTreatmentIsExist(
  siteType: string,
  clinicNumber: string,
  patientId: number,
  visitDates: string[]
): Promise<boolean> {
  const res = await apiClient.post<{ success: boolean; data: { exists: boolean } }>(
    clinicApiPath(siteType, clinicNumber, '/visits/treatment-is-exist'),
    { patient_id: patientId, visit_date: visitDates }
  );
  return res.data.data.exists;
}

/**
 * Check insurer special-calculation-cost period for one patient/date.
 * Mirrors backend VisitErrorChecker [10b] logic for modal pre-check.
 */
export async function checkInsurerSpecialCostOutOfRange(
  siteType: string,
  clinicNumber: string,
  patientId: number,
  visitDate: string
): Promise<{ outOfRange: boolean; message: string | null }> {
  const res = await apiClient.post<{
    success: boolean;
    data: { out_of_range: boolean; message: string | null };
  }>(clinicApiPath(siteType, clinicNumber, '/visits/insurer-special-cost-check'), {
    patient_id: patientId,
    visit_date: visitDate,
  });

  return {
    outOfRange: Boolean(res.data.data?.out_of_range),
    message: res.data.data?.message ?? null,
  };
}

/** Row returned from /visits/patient-day-visits — minimal fields for duplicate checks. */
export interface PatientDayVisit {
  id: number;
  patient_id: number;
  massager_id: number;
  visit_datetime_from: string;
  is_locked: boolean;
  is_suddenly_visit: boolean;
}

/**
 * Fetch all visits for a patient on a given date across ALL 施術師 in the clinic.
 * Used by the modal 適用 check to detect cross-massager duplicates that are hidden
 * from calendarVisits (which is scoped to the current 施術師).
 */
export async function fetchPatientDayVisits(
  siteType: string,
  clinicNumber: string,
  patientId: number,
  visitDate: string,
  visitMonth?: string,
  serviceType: string = SERVICE_TYPE_A2
): Promise<PatientDayVisit[]> {
  const res = await apiClient.get<{ success: boolean; data: { events: PatientDayVisit[] } }>(
    clinicApiPath(siteType, clinicNumber, '/visits/patient-day-visits'),
    {
      params: {
        patient_id: patientId,
        visit_date: visitDate,
        ...(visitMonth ? { visit_month: visitMonth } : {}),
      },
      headers: { 'X-Service-Type': serviceType },
    }
  );
  return res.data.data.events ?? [];
}

export async function printWeeklySchedule(
  siteType: string,
  clinicNumber: string,
  payload: PrintWeeklySchedulePayload
): Promise<Blob> {
  const response = await apiClient.post(
    clinicApiPath(siteType, clinicNumber, '/visits/schedule/print'),
    payload,
    { responseType: 'blob' }
  );
  return response.data as Blob;
}
